import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97] active:opacity-90 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:transition-transform active:[&_svg]:scale-95 " +
  // High-contrast focus ring for icon-only controls so they're obvious during tab navigation on any background
  "has-[>svg:only-child]:focus-visible:ring-[3px] has-[>svg:only-child]:focus-visible:ring-foreground has-[>svg:only-child]:focus-visible:ring-offset-[3px] has-[>svg:only-child]:focus-visible:ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground border border-primary hover:bg-background hover:text-primary hover:border-primary",
        destructive: "bg-destructive text-destructive-foreground border border-destructive hover:bg-background hover:text-destructive hover:border-destructive",
        outline: "border border-input bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary",
        secondary: "bg-secondary text-secondary-foreground border border-secondary hover:bg-background hover:text-secondary-foreground hover:border-secondary",
        ghost:
          "hover:bg-primary hover:text-primary-foreground " +
          // Subtle affordance for icon-only ghost buttons (any size)
          "has-[>svg:only-child]:bg-muted/40 has-[>svg:only-child]:border has-[>svg:only-child]:border-border/60 has-[>svg:only-child]:text-foreground has-[>svg:only-child]:shadow-sm " +
          "has-[>svg:only-child]:hover:bg-primary has-[>svg:only-child]:hover:text-primary-foreground has-[>svg:only-child]:hover:border-primary",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 min-h-11 md:min-h-10 px-4 py-2",
        sm: "h-9 min-h-11 md:min-h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10 min-h-11 min-w-11 md:min-h-10 md:min-w-10",
        "icon-sm": "h-8 w-8 min-h-11 min-w-11 md:min-h-8 md:min-w-8 rounded-md",
        "icon-lg": "h-11 w-11 rounded-md",
      },

    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
